import React, { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { withStyles } from 'tss-react/mui';

import EmailIcon from '@mui/icons-material/MailOutline';
import ExportIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TrashIcon from '../../../common/icons/TrashIcon';

import ConfirmationDialog from '@common/components/ConfirmationDialog';
import StyledDivider from '../../../common/components/misc/StyledDivider';
import ApiService from '../../../common/services/ApiService';
import { sendProfileExportEmail } from '../../api/profiles';
import { useProfileDomainsQuery } from '../../hooks/useProfile';
import { useUserQuery } from '../../hooks/useUser';
import ResponsesForm from './ResponsesForm';

const styles = theme => ({
  domainNavButtons: {
    padding: `${theme.typography.pxToRem(25)} 0`,
  },
  profileActionButton: {
    color: theme.typography.color.primary,
    cursor: 'pointer',
    '&:hover': {
      color: theme.typography.color.primary,
    },
    marginLeft: theme.typography.pxToRem(20)
  },
  profileName: {
    marginTop: theme.typography.pxToRem(15),
    marginBottom: theme.typography.pxToRem(25),
  },
  progressBar: {
    borderRadius: theme.typography.pxToRem(100),
  },
  progressText: {
    fontFamily: 'IBM Plex Sans, medium',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
    opacity: 0.7,
  },
});

const ManageResponses = ({ classes, profile, responseLabels, user, supervisor, onForceRefresh }) => {
  const [currentDomain, setCurrentDomain] = React.useState(null);
  const [disableNextDomain, setDisableNextDomain] = React.useState(true);
  const [disableSubmitProfile, setDisableSubmitProfile] = React.useState(false);
  const [formCompleted, setFormCompleted] = React.useState(false);
  const [domainIndex, setDomainIndex] = React.useState(0);
  const [progressBar, setProgressBar] = React.useState(profile.supervisor_completed_percent || 0);
  const [open, setOpen] = React.useState(false);
  const [responsesList, setResponsesList] = React.useState([]);
  const [responseMessage, setResponseMessage] = React.useState(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = React.useState(false);

  const api = useRef(new ApiService());

  const { data: supervisorData } = useUserQuery(supervisor?.id);
  const { data: profileDomains } = useProfileDomainsQuery(profile?.id);

  useEffect(() => {
    if (profileDomains && profileDomains.length > 0) {
      var currentDomainData = profileDomains[0];

      getFormData(currentDomainData.id);
      setDomainIndex(0);
    }
  }, [profileDomains]);

  useEffect(() => {
    setProgressBar(profile.completed ? 100 : profile.supervisor_completed_percent);
  }, [profile.id]);

  const checkTotalResponses = (capabilityCount, allResponses) => {
    var responsesCount = 0;
    Object.keys(allResponses).forEach(key => {
      if (allResponses[key].SupervisorResponse !== null) {
        responsesCount++;
      }
    });
    const disabled = responsesCount !== capabilityCount;
    setDisableNextDomain(disabled);
  };

  const supervisorEmail = useMemo(() => supervisorData?.email, [supervisorData]);

  const disableRadioBtn = useMemo(() => {
    return profile.supervisor_completed === true || profile.educator_completed === false || formCompleted;
  }, [profile.supervisor_completed, profile.educator_completed, formCompleted]);

  const exportProfile = useCallback(() => {
    sendProfileExportEmail(profile.id).then((response) => {
      setOpen(true);

      if (response && response.message) {
        setResponseMessage(response.message);
      } else {
        setResponseMessage('Error sending email');
      }
    }).catch((error) => {
      console.error('Failed to send profile export email:', error);
      setOpen(true);
      setResponseMessage('Error sending email');
    });
  }, [profile.id]);

  const formCompletionCheck = (progressPercentage, domainIndex) => {
    var formCompletionButtons = 'visible';
    const completedWithoutSupervisor = profile.completed && !supervisor.supervisor_completed;
    const disableNext = completedWithoutSupervisor ? false : disableNextDomain;
    const disableSubmit = completedWithoutSupervisor ? false : disableSubmitProfile;

    if (progressPercentage === 100 && domainIndex === (profileDomains?.length - 1)) {
      if (profile.completed === false) {
        formCompletionButtons = 'visible';
      }
      else if (profile.completed === true) {
        formCompletionButtons = 'hidden';
      }
      return (
        <Button
          color="secondary"
          disabled={disableSubmit}
          onClick={() => submitCompletedProfile()}
          style={{ visibility: formCompletionButtons }}
          variant="contained"
        >
          <Typography variant="button">Submit Profile</Typography>
        </Button>
      );
    } else {
      return (
        <Button
          color="secondary"
          disabled={disableNext}
          onClick={() => getNextDomain()}
          variant="contained"
        >
          <Typography variant="button">Next Domain</Typography>
        </Button>
      );
    }
  };

  const getFormData = (currentDomainId) => {
    const getQuestionsPromise = api.current.query(`/api/v1/profiles/${profile.id}/domains/${currentDomainId}`);
    const getResponsesPromise = api.current.query(`/api/v1/response_summary?profile_id=${profile.id}&domain_id=${currentDomainId}`);
    Promise.all([getQuestionsPromise, getResponsesPromise]).then(([domainData, responses]) => {
      const currentDomainData = domainData.data;
      const responsesListData = responses.data;

      setCurrentDomain(currentDomainData);
      setResponsesList(responsesListData);

      var capabilityCount = currentDomainData.capabilities.length;
      var allResponses = responsesListData;
      checkTotalResponses(capabilityCount, allResponses);
    }).catch(console.error);
  };

  const getNextDomain = () => {
    var newDomainIndex = domainIndex + 1;
    setDomainIndex(newDomainIndex);
    getFormData(profileDomains[newDomainIndex].id);
    scrollToTop();
  };

  const getPreviousDomain = () => {
    var newDomainIndex = domainIndex - 1;
    setDomainIndex(newDomainIndex);
    getFormData(profileDomains[newDomainIndex].id);
    scrollToTop();
  };

  const handleSnackbarClose = () => {
    setOpen(false);
    setResponseMessage(null);
  };



  const scrollToTop = () => {
    window.scroll({
      top: 100,
      behavior: "smooth",
    });
  };

  const submitCompletedProfile = () => {
    const url = '/api/v1/profiles/' + profile.id;
    api.current.put(url, { profile: { supervisor_completed: true } }).then((res) => {
      if (res.status === 200) {
        setDisableSubmitProfile(true);
        onForceRefresh();
        setFormCompleted(true);
        scrollToTop();
      } else {
        console.error('Unknown response', res);
      }
    }).catch(console.error);
  };

  const updateProgressBar = (updatedPercentage) => {
    setProgressBar(profile.completed ? 100 : updatedPercentage);
  };

  const updateResponses = (response, currentResponses) => {
    var capabilityId = response.capability_id;
    var responseType = response.type;
    var value = response.value;

    currentResponses[capabilityId] === undefined ?
      currentResponses = Object.assign({
        [capabilityId]:
        {
          ['BusinessIdealResponse']: null,
          ['EducatorResponse']: null,
          [responseType]: value
        }
      }, currentResponses) :
      currentResponses[capabilityId][responseType] = value;
    setResponsesList(currentResponses);

    var capabilityCount = currentDomain.capabilities.length;
    var allResponses = responsesList;
    checkTotalResponses(capabilityCount, allResponses);
    if (progressBar === 100) {
      setDisableSubmitProfile(false);
    }
  };

  const handleDeleteConfirm = () => {
    api.current.delete('profiles', profile.id).then((response) => {
      if (response.status === 400) {
        api.current.notifyModalError(response.json.message);
      } else {
        onForceRefresh();
        setDeleteAlertOpen(false);
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteAlertOpen(false);
  };

  var showPreviousDomain = domainIndex === 0 ? 'hidden' : 'visible';

  return (
    <Fragment>
      <Grid container alignItems="center">
        <Grid size={11}>
          <LinearProgress
            className={classes.progressBar}
            color="secondary"
            value={progressBar}
            valueBuffer={100}
            variant="determinate"
          />
        </Grid>
        <Grid size={1}>
          <Typography align="right" className={classes.progressText}>
            {`${progressBar}%`}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        className={classes.profileName}
        justifyContent="space-between"
      >
        <Typography variant="h6">
          {profile.educator_full_name}
        </Typography>
        <Grid>
          {profile.completed === false && (user.is_any_admin) &&
            <Tooltip title={`Delete this profile, this can't be undone. Admins only.`}>
              <a
                className={classes.profileActionButton}
                onClick={() => setDeleteAlertOpen(true)}
              >
                <TrashIcon />
              </a>
            </Tooltip>
          }
          <Tooltip title={`Email profile to ${supervisorEmail}`}>
            <a
              className={classes.profileActionButton}
              onClick={() => exportProfile()}
            >
              <EmailIcon style={{ opacity: 0.6 }} />
            </a>
          </Tooltip>
          <Tooltip title='Generate & open PDF profile in new tab.'>
            <a
              href={`/api/v1/profiles/${profile.id}.pdf?is_admin_app=true`}
              target='_blank'
              className={classes.profileActionButton}

            >
              <ExportIcon style={{ opacity: 0.6 }} />
            </a>
          </Tooltip>
        </Grid>
      </Grid>
      <StyledDivider />
      {currentDomain && responseLabels &&
        <ResponsesForm
          currentDomain={currentDomain}
          disableRadioBtn={disableRadioBtn}
          profile={profile}
          responsesList={responsesList}
          updateProgressBar={updateProgressBar}
          updateResponses={updateResponses}
          responseLabels={responseLabels}
        />
      }
      <Grid
        container
        className={classes.domainNavButtons}
        justifyContent="space-between"
      >
        <Button
          color="secondary"
          disabled={domainIndex === 0}
          onClick={() => getPreviousDomain()}
          style={{ visibility: showPreviousDomain }}
          variant="contained"
        >
          <Typography variant="button">Previous Domain</Typography>
        </Button>
        {formCompletionCheck(progressBar, domainIndex)}
      </Grid>
      <Snackbar
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        autoHideDuration={4000}
        message={<span>{responseMessage}</span>}
        onClose={handleSnackbarClose}
        open={open}
      />
      <ConfirmationDialog
        message="This will fully delete the educator & supervisor responses for this profiling tool. This operation can't be undone!"
        okLabel={`Delete ${profile.educator_full_name}'s profile`}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        open={deleteAlertOpen}
        title={`Delete ${profile.educator_full_name}'s profile?`}
      />
    </Fragment>
  );
};

export default withStyles(ManageResponses, styles, { withTheme: true });
